import { inject, Injectable } from "@angular/core";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	filter,
	forkJoin,
	map,
	Observable,
	of,
	switchMap,
	take
} from "rxjs";
import { OrdersRemoteService } from "@modules/limit-orders/remotes/orders.remote.service";
import {
	IDefaultPrice,
	IOrder,
	IOrdersList,
	IOrderTotal
} from "@modules/limit-orders/interfaces/limit-orders.remote.interface";
import {
	ILoadMoreParams,
	IOrderListModel,
	IOrderListScrollableFilterRequest,
	IOrderListWithError
} from "../interfaces/limit-orders.list.model";
import { WalletsBalancesFacadeService } from "@modules/+new/core/wallets-balances/domain/+state/wallets-balances.facade.service";
import { ButtonAction } from "@modules/discover/constants/actions";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { Fiat, Offer, Price } from "@models";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { FetchOrderStatus, OrderField, OrderType } from "@gql/offer";
import { LimitOrderNavigationFacade } from "./navigation.facade";
import { LimitOrderQueryParamName, LimitOrderRouteName } from "../interfaces/limit-orders-route.interface";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { SELECTED_CURRENCY } from "@models/trade-offer.model";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import {
	ICancelOrderPayload,
	IOrderSwitchVisibilityType,
	ISwitchSeenCookie
} from "../interfaces/limit-order.view.interface";
import { IFiatWalletDataViewModel } from "@modules/checkout/entities/checkout.fiat-selection.model";
import { CashPlusPortfolioStateService } from "@modules/cashplus/+state/cashplus-portfolio.state.service";
import { TranslationService } from "@core-services/translation.service";
import { ToastrService } from "ngx-toastr";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";
import { OrdersEntryService } from "@modules/limit-orders/services/orders-entry.service";
import { UserFiatFundsService } from "@modules/shared/services/user-fiat-funds.service";
import { FiatDepositEntryService } from "@modules/+new/payments/features/fiat-deposit/infrastructure/fiat-deposit-entry.service";
import { LegacyTradeEntryService } from "@modules/trade/helpers/legacy-trade-entry.service";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { Index } from "@models/asset/index/index.model";
import { OrdersCountFacade } from "@modules/limit-orders/services/orders-count.facade";
import { convertTradableToFiat, toEuroByFiatId } from "@helpers/tradable-convert";
import { UserService } from "@main-services/user.service";
import { Helper } from "@helpers/functions";
import { LIMIT_ORDERS_SWITCH_VIEWED } from "@helpers/constants";
import { Tradable } from "@models/interfaces/tradable";
import { ActionButtonConfig, ActionButtonsFacade } from "@modules/discover/domain/+state/action-buttons.facade";
import { mapEstimatePricesToPrice } from "@modules/trade/helpers/helper";
import {
	EstimatePricesGQLService,
	EstimatePricesQuery,
	EstimatePricesQueryVariables
} from "../../../api/graphql/offer/resources/generated/estimate.prices.service.graphql-gen";
import { ExceptionsService } from "@core-services/exceptions.service";
import { BFFAsset, BFFAssetsService } from "@core-services/bff-assets.service";
import { BFFSecurityAsset } from "@models/asset/bff_security_asset";
import { InfoService } from "@core-services/info.service";

export const INITIAL_PAGE = 1;
export const PAGE_SIZE = 10;

@Injectable({ providedIn: "root" })
export class LimitOrdersService {
	private launchDarkly = inject(LaunchDarklyService);
	private limitOrdersRemote = inject(OrdersRemoteService);
	private walletFacade = inject(WalletsBalancesFacadeService);
	private cryptoDepositState = inject(CryptocoinsState);
	private tradableListService = inject(TradableListService);
	private accountService = inject(AccountService);
	private readonly fiatService = inject(FiatService);
	private navigation = inject(LimitOrderNavigationFacade);
	private walletService = inject(TradableWalletListService);
	private apiService = inject(ApicallService);
	private fiatWalletService = inject(FiatWalletService);
	private confirm = inject(ConfirmModalService);
	private fiatwalletService = inject(FiatWalletService);
	private cashPlusPortfolioState = inject(CashPlusPortfolioStateService);
	private cashPlusFacade = inject(CashPlusFacade);
	private cashPlusPortfolioFacade = inject(CashplusPortfolioFacade);
	private userService = inject(UserService);
	private actionButtonsFacade = inject(ActionButtonsFacade);
	private bff = inject(BFFAssetsService);
	private infoService = inject(InfoService);

	private readonly translationService = inject(TranslationService);
	private readonly toastr = inject(ToastrService);
	private readonly checkoutEntryHelperService = inject(CheckoutEntryHelperService);
	private readonly orderEntryService = inject(OrdersEntryService);
	private readonly fundsService = inject(UserFiatFundsService);
	private readonly fiatDepositEntryService = inject(FiatDepositEntryService);
	private readonly legacyTradeEntryService = inject(LegacyTradeEntryService);
	private readonly ordersCountFacade = inject(OrdersCountFacade);
	private readonly estimatePricesService = inject(EstimatePricesGQLService);
	private exceptionsService = inject(ExceptionsService);

	isBuyLimitEnabledFF$ = this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_BUY);
	isSellLimitEnabledFF$ = this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_SELL);
	isTotalOrdersEnabledFF$ = this.launchDarkly.isActive$(LD_FLAG.LIMIT_ORDERS_HOME_ADP);
	switchVariationVisibility$ = this.launchDarkly.getFeatureConfig$<IOrderSwitchVisibilityType>(
		LD_FLAG.LIMIT_ORDERS_SWITCH_VARIATIONS
	);

	qp$ = this.navigation.getQueryParams$;
	loadMorePage$ = new BehaviorSubject<ILoadMoreParams>({ page: INITIAL_PAGE, pageSize: PAGE_SIZE });

	private orders: IOrder[] = [];

	public flushTempOrderList(): void {
		this.orders = [];
	}

	public isMaxOrdersReached$(): Observable<boolean> {
		return this.ordersCountFacade
			.getOrdersCount$()
			.pipe(map((orders) => orders.opened.buy + orders.opened.sell >= orders.maxOpenOrders));
	}

	public navigateToMarketOrderWithError(orderType: OrderType, assetId: string): void {
		this.limitOrdersReachedMessage();
		assetId && this.openMarketOrderFlow(orderType, assetId);
	}

	public navigateToAssetSelectionWithError(orderType: OrderType): void {
		if (this.orderEntryService.handleUnverifiedAndBusinessCases()) {
			return;
		}
		this.limitOrdersReachedMessage();
		if (orderType === OrderType.Buy) {
			this.orderEntryService.openAssetSelection(true);
		}
		if (orderType === OrderType.Sell) {
			this.legacyTradeEntryService.openLegacySell();
		}
	}

	public openMarketOrderFlow(orderType: OrderType, assetId: string): void {
		if (orderType === OrderType.Buy) {
			this.openAssetBuyMarket(assetId);
		}
		if (orderType === OrderType.Sell) {
			this.legacyTradeEntryService.openSellNotification(assetId);
		}
	}

	public openOrderTypeDecision(assetId?: string): void {
		this.navigation.gotoByNavigation(LimitOrderRouteName.LimitOrderTypeDecision, {
			[LimitOrderQueryParamName.AssetId]: assetId
		});
	}

	// This function accept optional assedId parameter, if assetId is not provided the user is redirect to the asset selection
	public openLimitOrderFlow(orderType: OrderType, assetId?: string): void {
		if (orderType === OrderType.Buy) {
			this.orderEntryService.openBuyOrderFlow(assetId);
		}
		if (orderType === OrderType.Sell) {
			this.orderEntryService.openSellOrderFlow(assetId);
		}
	}

	public limitOrdersReachedMessage(): void {
		this.toastr.error(this.translationService.translate("max_amount_of_orders_reached"));
	}

	public fetchOrderListScrollAble$(
		options: Partial<IOrderListScrollableFilterRequest>
	): Observable<IOrderListModel | IOrderListWithError> {
		const {
			status = FetchOrderStatus.Open,
			type: orderType = OrderType.Buy,
			order = OrderField.CreatedAt
		} = options;

		return combineLatest({
			loadMore: this.loadMorePage$.asObservable(),
			qp: this.navigation.getQueryParams$.pipe(take(1))
		}).pipe(
			switchMap(
				({ loadMore: { page, pageSize }, qp }) =>
					this.limitOrdersRemote
						.fetchOrdersList$(
							{
								page$: page,
								pagesize$: pageSize,
								assetId$: qp[LimitOrderQueryParamName.AssetdPid],
								status,
								order
							},
							orderType
						)
						.pipe(switchMap((list) => this.fetchTradablesViaBFF(list))) //EATR-4840: fix race condition
			),
			switchMap((list) =>
				combineLatest({
					list: of(list),
					walletBalances: this.walletFacade.getCryptoBalancesStatus$().pipe(take(1)), // walletFacade.getCryptoBalancesStatus$ is throwing tons of subscriptions, must take(1) to avoid it
					isMultichainEnabled: this.launchDarkly.isActive$(LD_FLAG.DEPOSIT_MULTICHAIN),
					cryptoDeposit: this.cryptoDepositState.availableCryptoDepositList$
				})
			),
			map(({ list, walletBalances, isMultichainEnabled, cryptoDeposit }) => {
				const defaultFiat = this.accountService.getDefaultFiat();

				const { totalItems } = list.pageInfo;

				this.orders = [...this.orders, ...list.orders];

				return this.createOrderListModel(
					this.orders,
					defaultFiat,
					walletBalances,
					isMultichainEnabled,
					cryptoDeposit,
					totalItems,
					this.orders.length
				);
			}),
			catchError(() =>
				of({
					hasError: true,
					unverifiedOrUnconfirmed: this.userService.isUnverified || this.userService.isWaitingForDocuments()
				})
			)
		);
	}

	public getActionButtonConfigs(
		tradable: Tradable,
		walletBalances,
		defaultFiat: Fiat,
		isMultichainEnabled: boolean,
		depositTradable: PaymentsCryptocoin,
		hasCancelOrder = false,
		marginTradingEnabled = false,
		marginTradingBuyEnabled: boolean = false
	): ActionButtonConfig[] {
		const highestBalanceWallet: TradableWallet = this.walletService.getHighestBalanceWallet(tradable.getId());
		const defaultWallet = this.walletService
			.getWalletsByTradableId(tradable?.getId())
			?.find((wallet) => wallet.isDefault());

		return this.actionButtonsFacade.getActionButtonConfigs(
			tradable,
			walletBalances,
			defaultFiat,
			highestBalanceWallet,
			defaultWallet,
			isMultichainEnabled,
			depositTradable,
			hasCancelOrder,
			marginTradingEnabled,
			marginTradingBuyEnabled
		);
	}

	// create order list
	private createOrderListModel(
		orders: IOrder[],
		defaultFiat: Fiat,
		walletBalances: WalletsBalanceStatus,
		isMultichainEnabled: boolean,
		availableDepositCryptocoins: PaymentsCryptocoin[],
		totalItems: number,
		loadedPages: number
	): IOrderListModel {
		const dictionary: IOrderListModel = {
			orderIds: [],
			ordersData: {},
			defaultFiat,
			totalItems,
			loadedPages
		};
		orders.forEach(({ orderId, assetId, price, amount, status, type, fiatId }) => {
			const tradable = this.tradableListService.getItemByPid(assetId);
			const depositCrypto = availableDepositCryptocoins.find((it) => it.cryptocoin_id.toString() === orderId);
			// getActionButtonConfigs has been augmented with hasCancelOrder
			const hasCancelOrder = true;
			const actionButtons = this.getActionButtonConfigs(
				tradable,
				walletBalances,
				defaultFiat,
				isMultichainEnabled,
				depositCrypto,
				hasCancelOrder
			);

			dictionary.orderIds.push(orderId);
			dictionary.ordersData[orderId] = {
				name: tradable.getName(),
				logo: tradable.getLogo(),
				symbol: tradable.getSymbol(),
				targetPrice: +price,
				amount: type === OrderType.Buy ? +amount : +amount * +price,
				estimatedPrice: type === OrderType.Buy ? +amount / +price : +amount,
				priceFiatPrecision: Math.max(2, tradable.getPrecisionFiat()),
				assetPrecision: tradable.getPrecision(),
				actionButtonBuy: actionButtons.find((a) => a.action === ButtonAction.BUY),
				actionButtonSell: actionButtons.find(
					(a) => a.isDisabled === !this.hasSellBalanceForAsset(tradable?.getId())
				),
				actionButtonsMore: actionButtons.filter(
					(a) => a.action === ButtonAction.ADD_ALERT || a.action === ButtonAction.CANCEL_ORDER
				),
				trxFiatId: fiatId,
				fiat: this.fiatService.findByPid(fiatId),
				tradable,
				status,
				type
			};
		});

		return dictionary;
	}

	/**
	 * Get default wallet with assetId
	 * @param assetId string
	 * @returns TradableWallet
	 */
	private getDefaultTradableWallet(assetId: string): TradableWallet {
		const selectedWallets: TradableWallet[] = this.getTradableWallets(assetId);
		return selectedWallets.find((w) => w.isDefault());
	}

	/**
	 * Get list of tradable wallets
	 * @param assetId
	 * @returns TradableWallet[]
	 */
	private getTradableWallets(assetId: string): TradableWallet[] {
		return this.walletService.getWalletsByTradableId(assetId) ?? [];
	}

	public defaultPriceDefaultPayloadRequest(fiatPid: string, assetPid: string): Observable<Offer> {
		const offer = new Offer();

		const fiat = this.fiatService.findByPid(fiatPid);

		const fiatWallet = this.fiatWalletService.getWalletByFiatId(fiat.getId());

		const fiatId = fiat.getId();
		const fiatWalletId = fiatWallet.getId();
		offer.setFiatWalletId(fiatWalletId);
		offer.setFiatId(fiatId);
		offer.setAmountDefinition(SELECTED_CURRENCY.CRYPTOCOIN);

		const tradable = this.tradableListService.getItemByPid(assetPid);

		if (tradable) {
			const assetId = tradable.getId();
			offer.setCurrencyId(assetId);
			const wallet = this.getDefaultTradableWallet(assetId);
			if (wallet) {
				const walletId = wallet.isNew() ? "" : wallet.getId();
				offer.setWalletId(walletId);
			}
			return of(offer);
		}

		return this.bff.getAssetFetch({ input: { pid: assetPid } }).pipe(
			map((asset: BFFAsset) => {
				const tradable = new BFFSecurityAsset(asset);
				const assetId = tradable.getId();
				offer.setCurrencyId(assetId);
				const wallet = this.getDefaultTradableWallet(assetId);
				if (wallet) {
					const walletId = wallet.isNew() ? "" : wallet.getId();
					offer.setWalletId(walletId);
				}
				return offer;
			})
		);
	}

	/**
	 * get default price list
	 * @param fiatPid - string
	 * @param assetPid - string
	 * @returns - an Observable of IOrder
	 */
	public getDefaultPriceRangeList(
		offer: Offer,
		switchDefaultToGraphql: boolean = false,
		estimatePricesPayload?: EstimatePricesQueryVariables
	): Observable<IDefaultPrice> {
		if (switchDefaultToGraphql) {
			return this.estimatePrices(estimatePricesPayload).pipe(
				map((price) => {
					return { price: mapEstimatePricesToPrice(price) } as IDefaultPrice;
				})
			);
		}
		const option = new ApicallOption(Price, offer);
		return this.apiService.post("trade/prices/default", option).pipe(
			map((response: Price[]) => ({
				price: response[0]
			}))
		);
	}

	public estimatePrices(input: EstimatePricesQueryVariables): Observable<EstimatePricesQuery["estimatePrices"]> {
		return this.estimatePricesService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { estimatePrices } }) => estimatePrices),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	cancelOrder(payload: ICancelOrderPayload, closeOnReturn = false): void {
		const { orderId, orderType, assetPid } = payload;

		this.navigation.closeModal();
		this.confirm
			.showConfirmModal(
				"confirm_cancel_order_title",
				"confirm_cancel_order_text",
				"cancel_order",
				"back",
				true,
				true,
				true
			)
			.subscribe((result) => {
				if (result) {
					//Goto cancel order
					this.navigation.gotoByNavigation(LimitOrderRouteName.LimitOrderCancel, {
						[LimitOrderQueryParamName.OrderId]: orderId,
						[LimitOrderQueryParamName.OrderType]: orderType,
						[LimitOrderQueryParamName.AssetdPid]: assetPid
					});
				} else {
					if (closeOnReturn) {
						this.navigation.closeModal();
					} else {
						// Return to order detail
						this.navigation.gotoByNavigation(LimitOrderRouteName.LimitOrderDetail, {
							[LimitOrderQueryParamName.OrderId]: orderId,
							[LimitOrderQueryParamName.OrderType]: orderType
						});
					}
				}
			});
	}

	/**
	 * get list of  available Fiat Wallets
	 * @returns FiatWalletData[] as Observable
	 */
	getFiatWalletList(): Observable<IFiatWalletDataViewModel[]> {
		return combineLatest({
			list: this.fiatwalletService.getList(),
			cashPlusState: this.cashPlusPortfolioState.getState$()
		}).pipe(
			map(({ list, cashPlusState }) => {
				return list
					.map((data) => {
						const fiat = this.fiatService.getItemById(data.getFiatId());
						let totalBalance = "0";

						// balance from cashplus state
						if (cashPlusState) {
							totalBalance =
								cashPlusState.fiatConfigs.find((c) => c.asset_pid === fiat?.getPid())?.asset_balance ??
								"0";
						}

						//balance from fiat wallet
						const balance = data.getBalance();

						// if balance is not available from cashplus state and wallet balance is available
						if (balance && totalBalance === "0") {
							totalBalance = balance.toString();
						}

						return { data, fiat, totalBalance };
					})
					.filter(({ fiat }) => !!fiat)
					.sort((a, b) => +b.totalBalance - +a.totalBalance);
			})
		);
	}

	/**
	 * get total orders for cache purpose
	 * @returns IOrderTotal[] as Observable
	 */
	getTotalOrders$(): Observable<IOrderTotal[]> {
		return this.limitOrdersRemote.totalOrder$();
	}

	private openAssetBuyMarket(assetId: string): void {
		this.launchDarkly
			.isActive$(LD_FLAG.CHECKOUT_FLOW_ENABLED)
			.pipe(take(1))
			.subscribe((isCheckoutFlowEnabled) => {
				if (isCheckoutFlowEnabled) {
					this.checkoutEntryHelperService.onTradableSelect(assetId);
				} else {
					if (this.fundsService.hasFiatWalletsWithFunds()) {
						this.legacyTradeEntryService.openLegacyBuyForAsset(assetId);
					} else {
						this.fiatDepositEntryService.openDeposit();
					}
				}
			});
	}

	/**
	 * check if cashplus is enabled
	 * @returns boolean as Observable
	 */
	isCashPlusOrderEnabled$(fiatPid: string): Observable<boolean> {
		return this.cashPlusFacade.isCashPlusEnabled$().pipe(
			switchMap((ffEnabled) => {
				if (ffEnabled) {
					return combineLatest({
						cashPlusSubscriptionPerFiatId:
							this.cashPlusPortfolioFacade.getPortfolioPerFiatStateUnFiltered$(fiatPid),
						cashPlusSubscriptionState: this.cashPlusFacade.getSubscriptionsState$()
					}).pipe(
						map(({ cashPlusSubscriptionPerFiatId, cashPlusSubscriptionState }) => {
							return !!cashPlusSubscriptionPerFiatId && cashPlusSubscriptionState?.subscribed;
						})
					);
				}

				return of(false);
			})
		);
	}
	// Get Sell Power per Account and per Asset
	public hasSellBalanceForAsset(assetId: string): boolean {
		const tradable = this.tradableListService.getItemById(assetId);
		const defaultFiat = this.accountService.getDefaultFiat();
		return this.walletService.getWalletsByTradableId(assetId)?.some((wallet) => {
			if (!tradable) {
				return false;
			}
			const walletAmount = convertTradableToFiat(tradable, wallet.getBalance(), defaultFiat);
			return toEuroByFiatId(walletAmount, defaultFiat) >= tradable.getMinSell();
		});
	}

	public hasSellBalance(): boolean {
		return this.getSellTradablesId().some((assetId) =>
			this.walletService.getWalletsByTradableId(assetId).map((wallet) => wallet.getBalance() > 0)
		);
	}

	private getSellTradablesId(): string[] {
		return this.walletService.list
			.map((wallet) => {
				const tradable = this.tradableListService.getItemById(wallet.getCurrencyId());
				if (!tradable) {
					return null;
				}

				const hasAboveMinSell =
					tradable instanceof Index
						? wallet.getBalance() >= tradable.getMinSell()
						: wallet.getBalance() * tradable.getAvgPrice() >= tradable.getMinSell();

				const isTradableValid =
					tradable.getAssetSettings()?.isPriceOrderAvailable() &&
					hasAboveMinSell &&
					tradable.isSellActive() &&
					!tradable.isIndexOnly() &&
					wallet.isDefault();

				return isTradableValid ? tradable.getId() : null;
			})
			.filter((id): id is string => id !== null);
	}

	public getCryptoBalances$(): Observable<WalletsBalanceStatus> {
		return this.walletFacade.getCryptoBalancesStatus$();
	}

	//cookie helper for switch visibility
	public navigateAwayAndUpdateSwitchVisilibityCookie(isInFlow = false, isAssetEligibleForLimitOrder = true): void {
		this.launchDarkly
			.getFeatureConfig$(LD_FLAG.LIMIT_ORDERS_SWITCH_VARIATIONS)
			.pipe(
				take(1),
				filter((config) => !!config)
			)
			.subscribe((config) => {
				// If asset is NOT eligible for limit order, then don't update the cookie
				if (!isAssetEligibleForLimitOrder) {
					return;
				}

				//show once
				if (config === IOrderSwitchVisibilityType.Show_Once) {
					Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, ISwitchSeenCookie.Seen, true);
				}

				//show always in flow
				if (config === IOrderSwitchVisibilityType.Show_Always && isInFlow) {
					Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, ISwitchSeenCookie.NotSeen, true);
				}

				//show always out of flow
				if (config === IOrderSwitchVisibilityType.Show_Always && !isInFlow) {
					Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, null, true);
				}
			});
	}

	fetchTradablesViaBFF(list: IOrdersList): Observable<IOrdersList> {
		const filteredList = this.filterSecurityAssetListFromOrderListHelper(list);

		if (filteredList.length > 0) {
			return this.fetchAssetsViaWithGenericBFF$(filteredList).pipe(
				switchMap((assets) => {
					this.addAssetsToTradableList(assets);
					return of(list);
				})
			);
		}

		return of(list);
	}

	filterSecurityAssetListFromOrderListHelper(list: IOrdersList): string[] {
		const initialPidList = list.orders.map((order) => order.assetId);
		// only used unique pid and filter pidList
		const uniquePidList = Array.from(new Set(initialPidList));
		const filteredList = uniquePidList.filter(
			(pid) => !this.tradableListService.getList().value.some((item) => item.getPid() === pid)
		);
		return filteredList;
	}

	fetchAssetsViaWithGenericBFF$(filteredList: string[]): Observable<BFFAsset[]> {
		const obsList$ = filteredList.map((pid) => this.bff.getAssetFetch({ input: { pid: pid } }).pipe(take(1)));

		return forkJoin(obsList$).pipe(take(1));
	}

	addAssetsToTradableList(assets: BFFAsset[]): void {
		const parsed = assets.map((asset) => new BFFSecurityAsset(asset));

		this.tradableListService.addItems(parsed);
	}
}
